import { Button, Flex, Icon, Label, ModalManagerContext, Text, Image, Link, Grid } from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import Switch from '@pega/cosmos-react-core/lib/components/Switch';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import { ConfirmationAlert } from '../../Utilities/CustomModal';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { authorizeFacebookPage, deleteAccount, getAccountDetails, updateAccount } from './FacebookApis';
import * as Logos from '../../../constants/images';
import { appState, confirmationAlertProps } from '../../../interfaces/common';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import { usePlatformVersionSelector } from '../../../redux/selectors';

type GetStarted = {
  enabled: boolean;
  payload: string;
};
type AccountDetails = {
  pageName: string;
  pageLink: string;
  lastUpdate: string;
  authorizedBy: string;
  authorized: boolean;
  subscriptions: string;
  getStarted?: GetStarted;
};

const Facebook: React.FC<any> = (props) => {
  const { id: connectionId }: any = useParams();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const initialState: AccountDetails = {
    pageName: '',
    pageLink: '',
    lastUpdate: '',
    authorizedBy: '',
    authorized: false,
    subscriptions: '',
  };
  const [accountDetails, setaccountDetails] = useState(initialState);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const deleteConfirmation = () => {
    modalMethods.current = create(
      ConfirmationAlert,
      {
        pageName: accountDetails?.pageName,
        deleteChannel: deleteChannel,
        ...props,
      },
      { alert: true },
    );
  };
  const { majorVersion, minorVersion } = usePlatformVersionSelector();
  const beyondEightDotSeven =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 7));

  const reauthorize = async () => {
    dispatch(updateLoaderDetails(true));
    const errorResponse = await authorizeFacebookPage(sessionId);
    if (errorResponse) {
      dispatch(updateBannerInfo(translate('failed_to_authorize_facebook'), 'urgent'));
    }
    dispatch(updateLoaderDetails(false));
  };

  const fetchAccountDetails = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await getAccountDetails(sessionId, connectionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      setaccountDetails(response?.data);
    } else {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, connectionId, navigate]);

  const deleteChannel = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await deleteAccount(connectionId, sessionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      navigate('/');
      dispatch(updateBannerInfo(`Facebook ${translate('account_deleted')}`, 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
  }, [dispatch, connectionId, sessionId, navigate, translate]);

  const updateGetStarted = async (bEnabled: boolean) => {
    let update: AccountDetails = { ...accountDetails };
    update.getStarted = {
      enabled: bEnabled,
      payload: '',
    };
    dispatch(updateLoaderDetails(true));
    let response = await updateAccount(connectionId, sessionId, update);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      dispatch(updateBannerInfo(`Facebook ${translate('account_updated')}`, 'success'));
      setaccountDetails(update);
    } else if (response?.status === 403) {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
  };

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  const headerElement = () => {
    return (
      accountDetails?.pageName && (
        <Text variant="h1" as="h1">
          {accountDetails?.pageName}
        </Text>
      )
    );
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      {accountDetails?.pageName && (
        <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
          <Grid
            container={{
              cols: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('connection')}</Label>
            <Flex
              container={{
                direction: 'row',
                itemGap: 1,
                alignItems: 'center',
              }}
            >
              <Text variant="secondary">{translate('Facebook')}</Text>
              <Image alt="Twitter" src={Logos.FacebookLogo} width="30px" height="40px" />
            </Flex>
          </Grid>

          <Grid
            container={{
              cols: 'repeat(2, 33% 67%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('page_link')}</Label>
            <Flex>
              <Link href={accountDetails?.pageLink} target="_blank">
                {accountDetails?.pageLink}
              </Link>
            </Flex>
          </Grid>

          {accountDetails?.authorizedBy && (
            <Grid
              container={{
                cols: 'repeat(2, 33% 67%)',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Label>{translate('authorized_by')}</Label>
              <Text variant="secondary">{accountDetails?.authorizedBy}</Text>
            </Grid>
          )}

          <Grid
            container={{
              cols: 'repeat(2, 33% 67%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('authorized')} </Label>
            <Flex
              container={{
                direction: 'row',
                itemGap: 1,
                alignItems: 'center',
              }}
            >
              <Icon
                style={{
                  color: accountDetails?.authorized ? 'green' : 'red',
                  fontSize: '1.5rem',
                }}
                name={accountDetails?.authorized ? 'check' : 'times'}
              ></Icon>
              {!accountDetails?.authorized && <Button onClick={reauthorize}>{translate('re_authorize')}</Button>}
            </Flex>
          </Grid>
          {beyondEightDotSeven && (
            <Grid
              container={{
                cols: 'repeat(2, 38% 62%)',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Switch
                id="get_started_switch"
                label={translate('get_started')}
                onChange={(e) => updateGetStarted(e.target.checked)}
                on={accountDetails.getStarted?.enabled}
              />
            </Grid>
          )}

          <Flex style={{ paddingTop: '0.5rem' }}>
            <Button onClick={deleteConfirmation}>{translate('delete_connection_button')}</Button>
          </Flex>
        </Flex>
      )}
    </SectionRenderTemplate>
  );
};

export default Facebook;
